import DOMPurify from "dompurify";

const RICH_TEXT_TAGS = [
  "b",
  "br",
  "div",
  "em",
  "i",
  "li",
  "ol",
  "p",
  "span",
  "strong",
  "u",
  "ul",
];

const RICH_TEXT_ATTRS = ["style"];

const ALLOWED_STYLE_PROPERTIES = new Set([
  "font-style",
  "font-weight",
  "margin-left",
  "padding-left",
  "text-align",
  "text-decoration",
  "text-decoration-line",
]);

const decodeHtmlEntities = (value = "") => {
  const stringValue = String(value);

  if (!/[&][a-zA-Z#0-9]+;/.test(stringValue)) {
    return stringValue;
  }

  if (typeof document === "undefined") {
    return stringValue
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = stringValue;
  return textarea.value;
};

const preservePlainTextLineBreaks = (value = "") => {
  const decodedValue = decodeHtmlEntities(value);
  const containsHtml = /<\/?[a-z][^>]*>/i.test(decodedValue);
  return containsHtml ? decodedValue : decodedValue.replace(/\r\n?|\n/g, "<br>");
};

const filterStyleAttributes = (html) => {
  if (typeof document === "undefined") return html;

  const template = document.createElement("template");
  template.innerHTML = html;

  template.content.querySelectorAll("[style]").forEach((element) => {
    const allowedStyles = [];

    Array.from(element.style).forEach((property) => {
      if (!ALLOWED_STYLE_PROPERTIES.has(property)) return;
      allowedStyles.push(`${property}: ${element.style.getPropertyValue(property)}`);
    });

    if (allowedStyles.length) {
      element.setAttribute("style", allowedStyles.join("; "));
    } else {
      element.removeAttribute("style");
    }
  });

  // Clipboard HTML often places indentation-only text nodes between list
  // items. Remove only those direct list children so pasted lists render
  // consistently without affecting meaningful spaces inside list items.
  template.content.querySelectorAll("ol, ul").forEach((list) => {
    Array.from(list.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
        node.remove();
      }
    });
  });

  return template.innerHTML;
};

export const sanitizeRichText = (value = "") =>
  filterStyleAttributes(
    DOMPurify.sanitize(preservePlainTextLineBreaks(value), {
      ALLOWED_TAGS: RICH_TEXT_TAGS,
      ALLOWED_ATTR: RICH_TEXT_ATTRS,
    })
  );

export const getRichTextPlainText = (value = "") => {
  if (typeof document === "undefined") {
    return String(value).replace(/<[^>]*>/g, " ");
  }

  const element = document.createElement("div");
  element.innerHTML = sanitizeRichText(value);
  return element.innerText || element.textContent || "";
};
